import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ThreadContext {
  goalId: string;
  projectId: string;
  workbookId: string; // Alias for projectId for page creation clarity
  projectTitle: string;
  hasProjectContext: boolean;
}

/**
 * Hook to get goal context from a thread ID
 * Uses the same pattern as ThreadItem.tsx for consistency
 */
export const useThreadContext = (threadId: string | null) => {
  return useQuery({
    queryKey: ['thread-context', threadId],
    queryFn: async (): Promise<ThreadContext | null> => {
      if (!threadId) return null;

      // First, get the thread metadata to check for project context and goalId
      const { data: thread, error: threadError } = await supabase
        .from('agent_threads')
        .select('metadata')
        .eq('id', threadId)
        .single();

      if (threadError) {
        console.warn('[useThreadContext] Failed to fetch thread:', threadError);
        return null;
      }

      // Check if this thread has project context (same pattern as ThreadItem.tsx)
      const metadata = thread.metadata as any; // Type assertion for metadata JSON
      const hasProjectContext = metadata?.projectContext && metadata?.goalId;
      if (!hasProjectContext) {
        console.log('[useThreadContext] Thread has no project context');
        return null;
      }

      const goalId = metadata.goalId;

      // Get project info via the junction table (same pattern as ThreadItem.tsx)
      const { data: projectData, error: projectError } = await supabase
        .from('planner_goals_workbooks')
        .select(`
          workbook:workbook_id (
            id,
            title
          )
        `)
        .eq('goal_id', goalId)
        .eq('agent_thread_id', threadId)
        .single();

      if (projectError || !projectData?.workbook) {
        console.warn('[useThreadContext] Failed to fetch project data:', projectError);
        return null;
      }

      return {
        goalId,
        projectId: projectData.workbook.id,
        workbookId: projectData.workbook.id, // Alias for clarity
        projectTitle: projectData.workbook.title,
        hasProjectContext: true
      };
    },
    enabled: !!threadId,
    staleTime: 5 * 60 * 1000, // 5 minutes - context doesn't change often
    retry: false, // Don't retry on failure - context either exists or doesn't
  });
};

/**
 * Direct function to get thread context (for use outside React components)
 */
export async function getThreadContext(threadId: string): Promise<ThreadContext | null> {
  if (!threadId) return null;

  try {
    // Get thread metadata
    const { data: thread, error: threadError } = await supabase
      .from('agent_threads')
      .select('metadata')
      .eq('id', threadId)
      .single();

    if (threadError || !thread) return null;

    // Check for project context
    const metadata = thread.metadata as any; // Type assertion for metadata JSON
    const hasProjectContext = metadata?.projectContext && metadata?.goalId;
    if (!hasProjectContext) return null;

    const goalId = metadata.goalId;

    // Get project info
    const { data: projectData, error: projectError } = await supabase
      .from('planner_goals_workbooks')
      .select(`
        workbook:workbook_id (
          id,
          title
        )
      `)
      .eq('goal_id', goalId)
      .eq('agent_thread_id', threadId)
      .single();

    if (projectError || !projectData?.workbook) return null;

    return {
      goalId,
      projectId: projectData.workbook.id,
      workbookId: projectData.workbook.id, // Alias for clarity
      projectTitle: projectData.workbook.title,
      hasProjectContext: true
    };
  } catch (error) {
    console.warn('[getThreadContext] Error:', error);
    return null;
  }
} 